import { useEffect } from "react";
import { Link } from "react-router";
import { useNavigate, useParams } from "react-router";

import {
  Column,
  ErrorText,
  LoadingIndicator,
  TopbarContainer,
  TopbarTitle,
} from "@/components";
import { SignupVariantsForm } from "@/components/tools/SignupVariantsForm";

import { useSetTitle } from "@/hooks/useSetTitle";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS, MIXPANEL_SUBSCREENS } from "@/lib/mixpanel/types";
import { novaClient } from "@/lib/trpc";
import { CreateOrUpdateSignupVariantRequest } from "@/types";

export const ToolsSignupVariantsEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("No ID found in URL for signup variant edit page!");
  }

  const { data: signupVariant, status } =
    novaClient.internal.admin.dynamicPageVariants.findOne.useQuery({
      id: parseInt(id),
    });

  const { mutate: updateSignupVariant, isPending } =
    novaClient.internal.admin.dynamicPageVariants.update.useMutation({
      onSuccess: () => {
        void navigate(`/tools/signup-variants/${id}`);
      },
    });

  useSetTitle({
    title: "Tools | Signup Variants | Edit",
  });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.SIGNUP_VARIANTS, {
      subScreenName: MIXPANEL_SUBSCREENS.EDIT,
    });
  }, []);

  const handleSubmit = (data: CreateOrUpdateSignupVariantRequest) => {
    updateSignupVariant({ id: parseInt(id), ...data });
  };

  return (
    <Column className="h-svh">
      <TopbarContainer>
        <TopbarTitle className="!text-slate-500">
          <Link className="hover:underline" to="/tools/signup-variants">
            Signup Variants
          </Link>{" "}
          /{" "}
          <Link className="hover:underline" to={`/tools/signup-variants/${id}`}>
            {id}
          </Link>{" "}
          / <span className="text-slate-700">Edit</span>
        </TopbarTitle>
      </TopbarContainer>
      <div className="flex-1 overflow-hidden">
        {status === "pending" && (
          <div className="p-6"><LoadingIndicator variant="medium" /></div>
        )}
        {status === "error" && (
          <div className="p-6"><ErrorText errorMessage="Error loading signup variant." /></div>
        )}
        {status === "success" && (
          <SignupVariantsForm
            isSubmitting={isPending}
            onSubmit={handleSubmit}
            signupVariant={signupVariant}
            submitButtonText="Update"
          />
        )}
      </div>
    </Column>
  );
};
